import SectionTitle from "../components/SectionTitle";
import PageSeo from "../components/PageSeo";
import Reveal from "../components/Reveal";
import { ASSETS } from "../mock";
import { Heart, Sparkles, Users, Award, Compass, Gift, HandHeart, Flame } from "lucide-react";

const MILESTONES = [
  {
    date: "2023",
    title: "Nace Lia",
    body: "Laura da a luz a Lia, una pequeña mariposa que ilumina a toda la familia.",
    icon: Sparkles,
  },
  {
    date: "4 meses",
    title: "El diagnóstico",
    body: "Al cumplir Lia los 4 meses, la familia recibe el diagnóstico de Complejo de Esclerosis Tuberosa (C.E.T.).",
    icon: Compass,
  },
  {
    date: "15/06/2024",
    title: "Primeros eventos en Sant Joan",
    body: "Se organizan eventos en Sant Joan d'Alacant para dar visibilidad al CET y apoyar a Lia. Surge la idea de fundar una asociación.",
    icon: Users,
  },
  {
    date: "2024",
    title: "Fundación de Pequeño Fresno",
    body: "Laura, con el apoyo incondicional de Lilian (tesorería) y Mayo (secretaría), formalizan la creación de la Asociación Pequeño Fresno.",
    icon: Award,
  },
  {
    date: "2024",
    title: "Primeros eventos a favor de Pequeño Fresno",
    icon: Gift,
    variant: "solidarity",
    accent: "sun",
    items: [
      "Donación de Vanessa",
      "Caminata solidaria de Sant Joan d'Alacant",
      "Donación del FCOAMI",
      "Donación de la residencia Ballesol",
    ],
  },
  {
    date: "2025",
    title: "Más solidaridad",
    icon: HandHeart,
    variant: "solidarity",
    accent: "leaf",
    items: [
      "Donación de Eureka!",
      "Eventos en la Escuela Europea de Alicante",
      "Donación del FCOAMI",
      "Donación de Vanessa",
      "Eventos solidarios de Sant Joan d'Alacant",
    ],
  },
  {
    date: "2026",
    title: "La solidaridad no para",
    icon: Flame,
    variant: "solidarity",
    accent: "coral",
    items: [
      "Donación de Eureka!",
      "Donación del FCOAMI",
      "Donación de Vanessa",
      "Eventos solidarios del grupo alicantino Forty",
    ],
  },
  {
    date: "Hoy",
    title: "Comunidad creciendo",
    body: "Familia, amigos y simpatizantes se suman a nuestra causa. Cada gesto y cada evento nos motivan a seguir volando.",
    icon: Heart,
  },
];

// Paletas por año (para las tarjetas solidarias)
const ACCENTS = {
  sun:   { bg: "linear-gradient(135deg, #fff3d6 0%, #ffe0a3 100%)", ring: "#f0b64f", chip: "#c88614", dot: "#f0b64f", glow: "#f6c56b" },
  leaf:  { bg: "linear-gradient(135deg, #eaf1d7 0%, #cddab6 100%)", ring: "#7a8e5a", chip: "#4d6a3a", dot: "#7a8e5a", glow: "#a5b58e" },
  coral: { bg: "linear-gradient(135deg, #ffe7dd 0%, #ffcbb5 100%)", ring: "#e8825a", chip: "#b85535", dot: "#e8825a", glow: "#f2a488" },
};

export default function Nosotros() {
  return (
    <div>
      <PageSeo
        title="Acerca de nosotros"
        description="Conoce el origen y la historia de la Asociación Pequeño Fresno: cómo nació y quiénes formamos parte de este proyecto solidario en apoyo al CET."
        path="/nosotros"
      />
      <section className="max-w-5xl mx-auto px-6">
        <SectionTitle>~ origen ~</SectionTitle>

        <Reveal>
          <div className="pf-soft rounded-3xl p-8 sm:p-12 relative overflow-hidden">
            <img src={ASSETS.leafDeco} alt="" className="absolute -right-10 -top-10 w-40 opacity-60" />
            <p className="prose-body text-[color:var(--text)] text-lg">
              Laura, madre orgullosa de 2 magníficos adolescentes, vuelve a ser madre en 2023 de la pequeña Lia. Al cumplir Lia los 4 meses, Laura aprende que su niña padece de una enfermedad rara llamada Complejo de Esclerosis Tuberosa (C.E.T.). Desde entonces, Laura y su familia aprenden a conocer la enfermedad, a aceptar su presencia y gestionan ese miedo por el qué será.
            </p>
          </div>
        </Reveal>

        <SectionTitle className="!my-20">··· nuestra historia ···</SectionTitle>

        {/* Timeline */}
        <div className="relative">
          {/* Central vertical line */}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-0.5 md:-translate-x-1/2" style={{ background: "linear-gradient(to bottom, transparent, var(--brand-light), var(--brand), var(--brand-light), transparent)" }} />

          <div className="space-y-12">
            {MILESTONES.map((m, i) => (
              <TimelineItem key={i} milestone={m} index={i} />
            ))}
          </div>
        </div>

        {/* Logo */}
        <Reveal>
          <div className="flex justify-center mt-20 mb-6">
            <div className="relative">
              <div className="absolute inset-0 rounded-full pf-gradient blur-3xl opacity-40 scale-90" aria-hidden="true" />
              <img
                src={ASSETS.logo}
                alt="Logo oficial Asociación Pequeño Fresno · Esclerosis Tuberosa"
                className="relative w-64 sm:w-80 md:w-96 h-auto drop-shadow-lg"
              />
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}

function TimelineItem({ milestone, index }) {
  const Icon = milestone.icon;
  const isRight = index % 2 === 1;
  const isSolidarity = milestone.variant === "solidarity";
  const accent = isSolidarity ? ACCENTS[milestone.accent] || ACCENTS.leaf : null;

  return (
    <Reveal delay={index * 100}>
      <div className={`relative flex md:items-center gap-6 md:gap-0 ${isRight ? "md:flex-row-reverse" : ""}`}>
        {/* Icon on line */}
        <div className="absolute left-6 md:left-1/2 md:-translate-x-1/2 z-10">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg ring-4 ring-white"
            style={{
              background: isSolidarity
                ? `linear-gradient(135deg, ${accent.glow}, ${accent.ring})`
                : "linear-gradient(135deg, var(--brand-light), var(--brand))",
            }}
          >
            <Icon className="w-5 h-5 text-white" />
          </div>
          {isSolidarity && (
            <span
              className="absolute inset-0 rounded-full animate-ping opacity-30"
              style={{ background: accent.ring }}
              aria-hidden="true"
            />
          )}
        </div>

        {/* Content card */}
        <div className={`ml-20 md:ml-0 md:w-1/2 ${isRight ? "md:pl-12" : "md:pr-12"}`}>
          {isSolidarity ? (
            <SolidarityCard milestone={milestone} accent={accent} />
          ) : (
            <div className="pf-soft rounded-2xl p-6 hover-lift shadow-md">
              <div className="text-xs font-semibold uppercase tracking-wider text-[color:var(--accent)]">{milestone.date}</div>
              <h3 className="h-serif text-2xl font-semibold text-[color:var(--brand-strong)] mt-1">{milestone.title}</h3>
              <p className="prose-body mt-2 text-[color:var(--text)] text-sm">{milestone.body}</p>
            </div>
          )}
        </div>

        <div className="hidden md:block md:w-1/2" />
      </div>
    </Reveal>
  );
}

function SolidarityCard({ milestone, accent }) {
  return (
    <div
      className="relative rounded-2xl p-6 shadow-md hover-lift overflow-hidden group"
      style={{
        background: accent.bg,
        border: `1px solid ${accent.ring}33`,
      }}
    >
      {/* Decorative glow */}
      <div
        className="absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl opacity-40 group-hover:opacity-60 transition-opacity"
        style={{ background: accent.glow }}
        aria-hidden="true"
      />
      {/* Year chip */}
      <div className="relative flex items-center justify-between gap-2 flex-wrap">
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider text-white shadow-sm"
          style={{ background: accent.chip }}
        >
          <Gift className="w-3 h-3" /> {milestone.date}
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: accent.chip }}>
          ✦ solidaridad
        </span>
      </div>

      <h3 className="h-serif text-2xl font-semibold mt-3" style={{ color: accent.chip }}>
        {milestone.title}
      </h3>

      <ul className="mt-4 space-y-2 relative">
        {milestone.items.map((it, i) => (
          <li
            key={i}
            className="flex items-start gap-3 text-sm text-[color:var(--text)] opacity-0"
            style={{
              animation: "pf-fade-slide .5s ease forwards",
              animationDelay: `${120 + i * 90}ms`,
            }}
          >
            <span
              className="mt-1.5 shrink-0 w-2 h-2 rounded-full"
              style={{ background: accent.dot, boxShadow: `0 0 0 3px ${accent.dot}22` }}
            />
            <span>{it}</span>
          </li>
        ))}
      </ul>

      {/* Ribbon "¡Millones de gracias!" */}
      <div className="relative mt-5 pt-4 border-t border-dashed" style={{ borderColor: `${accent.ring}55` }}>
        <div
          className="inline-flex items-center gap-2 font-cursive text-2xl leading-none"
          style={{ color: accent.chip }}
        >
          <HandHeart className="w-5 h-5" style={{ color: accent.chip }} />
          ¡Millones de gracias!
        </div>
      </div>

      <style>{`
        @keyframes pf-fade-slide {
          from { opacity: 0; transform: translateX(-8px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
